import React, { useState, useEffect, useCallback } from 'react';
import { Typography, Spin, List, Button, notification } from 'antd';
import { SolgariApiService } from '@/services/solgari-api/solgari-api.service';
import { IAgentApi } from '@/services/solgari-api/interfaces/agent.interface';
import { IPresenceData, SipJs } from '@/services/sip-js.service';
import { getExtensionFromUri } from '@/shared/helpers/get-from-uri.helper';
import { PresencesSorted } from '@/shared/constants/SortedPresences';
import { Presence } from '@/components/Presence/Presence';
import { EPresenceStatusApi } from '@/shared/enums/presence.enum';
import { debounce } from 'lodash';
import { MessageIcon } from '@/components/MessageIcon/MessageIcon';
import { commonMakeCall } from '@/shared/helpers/common-make-call.helper';
import InputSearch from '@/components/InputSearch/InputSearch';
import answerIcon from '@/public/icons/answer.svg';
import {
  SEARCH_DEBOUNCE_MS,
  CALL_PAGE_TYPE,
} from '@/shared/constants/callPage';
import { useStore } from '@/stores';
import './Phonebook.scss';
import { logger } from '@/services/logger.service';

const { Title } = Typography;

const CallIcon = <MessageIcon svgUrl={answerIcon} />;

export const Phonebook = () => {
  const {
    callStore: { callPage },
  } = useStore();

  const { page } = callPage;
  const [notificationApi, contextHolder] = notification.useNotification();
  const ownExtensionId: string = getExtensionFromUri(SipJs.authData.uri);
  const [persistedAgentsList, setPersistedAgentsList] = useState<IAgentApi[]>(
    [],
  );
  const [agentsList, setAgentsList] = useState<IAgentApi[]>([]);
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [extensionPresences, setExtensionPresences] = useState<
    Record<string, EPresenceStatusApi>
  >({});
  const [filteredByTextAgents, setFilteredByTextAgents] = useState<IAgentApi[]>(
    [],
  );
  const [loading, setLoading] = useState<boolean>(false);
  const [isSorted, setIsSorted] = useState<boolean>(false);

  useEffect(() => {
    loadAgents();
    return () => SipJs.makeUnSubscriptions(persistedAgentsList);
  }, []);

  useEffect(() => {
    const presenceSubscription = SipJs.presences$.subscribe(
      (presences: IPresenceData[]) => {
        const updatedPresences = getPresenceByExtensions(
          persistedAgentsList,
          presences,
        );
        setExtensionPresences(updatedPresences);

        const sortedAgents: IAgentApi[] = sortAgents(
          persistedAgentsList,
          updatedPresences,
        );
        setAgentsList(sortedAgents);
        setIsSorted(true);
      },
    );

    return () => presenceSubscription.unsubscribe();
  }, [persistedAgentsList]);

  const getPresenceByExtensions = (
    agents: IAgentApi[],
    presenceData: IPresenceData[],
  ): Record<string, EPresenceStatusApi> => {
    return agents.reduce(
      (result: Record<string, EPresenceStatusApi>, agent: IAgentApi) => {
        const presenceStatus = presenceData.find(
          (presence: IPresenceData) =>
            presence.extension === String(agent.extensionId),
        )?.status;
        result[agent.extensionId] = presenceStatus;
        return result;
      },
      {} as Record<string, EPresenceStatusApi>,
    );
  };

  const loadAgents = async () => {
    setLoading(true);
    try {
      const allAgents: IAgentApi[] = await SolgariApiService.getListOfAgents();
      const agents: IAgentApi[] = allAgents.filter(
        (agent: IAgentApi) =>
          String(agent.extensionId) !== ownExtensionId && !!agent.presence,
      );
      SipJs.makeSubscriptions(agents);
      setPersistedAgentsList(agents);

      const initiallySortedAgents = sortAgents(
        agents,
        {} as Record<string, EPresenceStatusApi>,
      );
      setAgentsList(initiallySortedAgents);
      setIsSorted(true);
    } catch (error) {
      logger.error('Failed to load contacts:', error);
    } finally {
      setLoading(false);
    }
  };

  const onSearch = useCallback(
    debounce((e: React.ChangeEvent<HTMLInputElement>) => {
      const { value } = e.target;
      const searchValue = value.trim().toLowerCase();
      setSearchQuery(searchValue);

      if (!searchValue) {
        setFilteredByTextAgents([]);
        return;
      }

      const searchFiltered: IAgentApi[] = agentsList.filter(
        (agent: IAgentApi) =>
          agent.fullName.toLowerCase().includes(searchValue) ||
          agent.extensionId.toString().includes(searchValue),
      );
      setFilteredByTextAgents(searchFiltered);
    }, SEARCH_DEBOUNCE_MS),
    [agentsList],
  );

  const sortAgents = (
    agents: IAgentApi[],
    presenceMap: Record<string, EPresenceStatusApi>,
  ): IAgentApi[] => {
    const getPresenceOrder = (status: EPresenceStatusApi): number => {
      const index = PresencesSorted.indexOf(status);
      return index === -1 ? Number.MAX_SAFE_INTEGER : index;
    };

    return [...agents].sort((a: IAgentApi, b: IAgentApi) => {
      const aPresence: EPresenceStatusApi = presenceMap[a.extensionId]
        ? presenceMap[a.extensionId]
        : a.presence;
      const bPresence: EPresenceStatusApi = presenceMap[b.extensionId]
        ? presenceMap[b.extensionId]
        : b.presence;

      const presenceDiff =
        getPresenceOrder(aPresence) - getPresenceOrder(bPresence);

      return presenceDiff || a.fullName.localeCompare(b.fullName);
    });
  };

  const getActualPresence = (agent: IAgentApi): EPresenceStatusApi =>
    extensionPresences[agent.extensionId] ?? agent.presence;

  const onStartCall = async (agent: IAgentApi) => {
    const agentExtensionId = agent.extensionId.toString();
    const agentPresence: EPresenceStatusApi = getActualPresence(agent);
    if (agentPresence === EPresenceStatusApi.AVAILABLE) {
      const { state } = await navigator.permissions.query({
        name: 'microphone' as PermissionName,
      });
      if (state !== 'granted') {
        notificationApi.error({
          message: 'Microphone permission is required',
          placement: 'bottomRight',
          duration: 5,
          className: 'notifications',
        });

        return;
      }
      if (SipJs.sessions?.length) {
        return;
      }

      commonMakeCall([], agentExtensionId);
    }
  };

  const displayedAgents = searchQuery ? filteredByTextAgents : agentsList;

  return (
    <div
      className={`phonebook-wrapper ${page === CALL_PAGE_TYPE.MINIMIZED ? 'shortened' : ''}`}
    >
      {contextHolder}
      <Title level={5}>Internal Phone Book</Title>
      <InputSearch
        onChange={onSearch}
        data-testid="phonebook-search-input"
        placeholder="Search by name or number"
      />

      {loading && !isSorted ? (
        <Spin className="spinner" />
      ) : (
        <div className="phonebook-list-wrapper">
          <List
            loading={loading}
            dataSource={displayedAgents}
            renderItem={(agent: IAgentApi) => (
              <List.Item
                onClick={() => onStartCall(agent)}
                key={agent.extensionId}
                className={`phonebook-list-item ${
                  getActualPresence(agent) === EPresenceStatusApi.AVAILABLE
                    ? 'available'
                    : 'unavailable'
                }`}
              >
                <Button
                  disabled={
                    getActualPresence(agent) !== EPresenceStatusApi.AVAILABLE
                  }
                  data-testid="phonebook-call-button"
                  className="call-details-button"
                  type="primary"
                  shape="circle"
                  icon={CallIcon}
                  onClick={() => null}
                ></Button>
                <List.Item.Meta
                  title={agent.fullName}
                  description={agent.extensionId}
                />
                <div className="phonebook-agent">
                  <Presence
                    status={getActualPresence(agent)}
                    isSelectable={false}
                  ></Presence>
                </div>
              </List.Item>
            )}
          />
        </div>
      )}
    </div>
  );
};
